import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { HeadlineTicker } from "../components";
import PilotApplicationForm from "../components/composite/PilotApplicationForm";
import usePageMeta from "../hooks/usePageMeta";

/**
 * /register - the pilot application page.
 *
 * RepCheck is invite-only during the pilot, so this route no longer creates
 * accounts. Anyone arriving here (including from an old bookmark or a search
 * result promising signup) gets told plainly that access is by application, and
 * can apply without navigating anywhere else. Accounts are created by a global
 * admin approving the application; the applicant then receives an emailed link
 * to set their own password.
 */
export default function Register() {
  usePageMeta({
    title: "Apply for the pilot · RepCheck",
    description:
      "RepCheck is currently in a closed pilot. Apply for access and we'll be in touch.",
  });

  return (
    <div className="min-h-screen w-full bg-card flex flex-col">
      {/* Top bar */}
      <div className="flex justify-between items-center px-6 sm:px-10 pt-6 pb-2">
        <Link to="/" className="flex items-center text-[2rem] font-bold font-roboto">
          <img src="/favicon.svg" alt="RepCheck logo" width="32" height="32" className="h-8 w-8 mr-2" />
          RepCheck
        </Link>
        <span className="text-lg font-semibold font-roboto">
          <Link to="/login" className="text-brand-600 hover:text-brand-700">Sign in</Link>
        </span>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-10">
        <div className="bg-card border border-border rounded-2xl shadow-card-hover px-6 sm:px-10 py-10 max-w-md w-full">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700 dark:bg-brand-500/10 dark:text-brand-200">
            <ShieldCheck className="h-3.5 w-3.5" /> Closed pilot
          </span>

          <h1 className="mt-4 text-3xl font-semibold text-foreground">
            Apply for access
          </h1>
          <p className="mt-2 text-base text-muted-foreground">
            RepCheck is in a closed pilot while we work closely with a small group of
            early customers. Tell us a little about you and we'll be in touch - approved
            applicants get an email with a link to set up their account.
          </p>

          <PilotApplicationForm submitLabel="Apply for access" />

          <div className="mt-8 border-t border-border pt-5 text-center text-base">
            <span className="text-muted-foreground">Already have an account? </span>
            <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
              Sign in
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Banner */}
      <div className="w-full">
        <HeadlineTicker />
      </div>
    </div>
  );
}
